import {parseSectionWeekId, type SectionWeekId} from '@cip/core/section';
import type {CourseId} from '@cip/core/types';
import {isNullOrUndefined} from 'utils/general';
import type {SectionId} from '../utils';

/** One section week whose SL debrief a roadmap row stands for. */
export interface SectionDebriefTarget {
  /** The section the debrief is for. */
  sectionId: SectionId;
  /** 1-based section week. */
  sectionWeek: SectionWeekId;
}

/**
 * Decides whether an SL debrief document records a submission. The document
 * can exist with only autosave draft fields, so existence alone does not
 * count: it needs `submittedAtMs`, or `responses` for documents written
 * before that marker existed.
 *
 * @param params.exists - Whether the debrief document exists.
 * @param params.data - The document's data; anything that is not an object
 *   counts as no submission.
 * @return True when the debrief was submitted.
 * @throws Never.
 */
export function isSLDebriefSubmitted({
  exists,
  data,
}: {
  exists: boolean;
  data: unknown;
}): boolean {
  if (!exists || data === null || typeof data !== 'object') return false;
  const submittedAtMs = 'submittedAtMs' in data ? data.submittedAtMs : null;
  const responses = 'responses' in data ? data.responses : null;
  return !isNullOrUndefined(submittedAtMs) || !isNullOrUndefined(responses);
}

/**
 * Resolves the debrief a raw "Section Debrief" roadmap item stands for: the
 * SL's own section in the item's week.
 *
 * @param params.completionId - The item's completion id, which holds its
 *   1-based section week.
 * @param params.ownSectionId - The SL's own section. Omitted when unknown.
 * @return The own section and week, or `undefined` when the own section is
 *   unknown or the completion id is not a valid section week.
 * @throws Never.
 */
export function getSectionDebriefItemTarget({
  completionId,
  ownSectionId,
}: {
  completionId: unknown;
  ownSectionId?: SectionId;
}): SectionDebriefTarget | undefined {
  const sectionWeek = parseSectionWeekId(completionId);
  if (sectionWeek === undefined || ownSectionId === undefined) {
    return undefined;
  }
  return {sectionId: ownSectionId, sectionWeek};
}

/**
 * Builds the link for a "Section Debrief" roadmap item: the own section's
 * debrief for the item's week, or the week-only route, which picks the
 * section itself, when the own section is unknown.
 *
 * @param params.courseId - Course of the roadmap.
 * @param params.completionId - The item's completion id, which holds its
 *   1-based section week.
 * @param params.ownSectionId - The SL's own section. Omitted means the
 *   week-only route.
 * @return The debrief path, or the section page when the completion id is
 *   not a valid section week.
 * @throws Never.
 */
export function getSectionDebriefItemPath({
  courseId,
  completionId,
  ownSectionId,
}: {
  courseId: CourseId;
  completionId: unknown;
  ownSectionId?: SectionId;
}): string {
  const sectionWeek = parseSectionWeekId(completionId);
  if (sectionWeek === undefined) return `/${courseId}/section`;
  if (ownSectionId === undefined) {
    return `/${courseId}/section/sl-section-debrief/${sectionWeek}`;
  }
  return `/${courseId}/section/${ownSectionId}/sl-section-debrief/${sectionWeek}`;
}
